import { Button, Tooltip } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { updateStatus } from '../../../api/transitions';
import { updateIsCanceled } from '../../../redux/slices/transitions';
import { enqueueSnackbar } from '../../../reusable-components/snackbar';
import { buttonStyle } from './TransitionStyles';

UpdateStatusButtons.propTypes = {
  data: PropTypes.object,
  setData: PropTypes.func,
  isAddingFac: PropTypes.bool,
  proceedButtonLabel: PropTypes.string,
};

export default function UpdateStatusButtons({ data, setData, isAddingFac = false, proceedButtonLabel }) {
  const navigate = useNavigate();
  const {
    formState: { isDirty },
  } = useFormContext();
  const {
    data: { statusConfig, statusConfigSortedKeys, currentStatusByPortfolio },
  } = useSelector((state) => state.transitions);
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const { id } = useParams();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const showButtons = useMemo(() => {
    const pageRouteName = Object.values(statusConfig)?.find(
      (x) => x.id === currentStatusByPortfolio[id]
    )?.pageRouteName;

    return pathname?.includes(`/${pageRouteName}/`) && data.isAdmin;
  }, [currentStatusByPortfolio, pathname, data, statusConfig, id]);

  const updatePortfolioStatus = async () => {
    const statusIndex = statusConfigSortedKeys.indexOf(data.statusId);
    const newStatusId = statusConfigSortedKeys[statusIndex + 1];

    await updateStatus(id, newStatusId)
      .then((resp) => {
        navigate(`/dashboard/transitions/${statusConfig?.[resp.data]?.pageRouteName}/${id}?facilityId=${facilityId}`);

        setData({ ...data, statusId: resp.data });
      })
      .catch(() => {
        enqueueSnackbar('Error updating status', { variant: 'error' });
      });
  };

  const cancelPortfolio = async () => {
    await dispatch(updateIsCanceled(id, !data?.isCanceled))
      .then((resp) => {
        if (resp) {
          enqueueSnackbar('Portfolio canceled successfully', { variant: 'success' });
          navigate('/dashboard/transitions/home');
        }
        setData({ ...data, isCanceled: resp });
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const proceedButtonDisabledMessage = useMemo(() => {
    if (data?.isCanceled) return 'Portfolio canceled';
    if (isAddingFac) return 'Save or delete the new facility before proceeding';
    if (isDirty) return 'Unsaved changes';
    return null;
  }, [isAddingFac, isDirty, data.isCanceled]);

  const cancelButtonDisabledMessage = useMemo(() => {
    if (isAddingFac) return 'Save or delete the new facility before proceeding';
    if (isDirty) return 'Unsaved changes';
    return null;
  }, [isAddingFac, isDirty]);

  return (
    showButtons && (
      <>
        <Tooltip
          placement="left"
          arrow
          sx={{ fontSize: '1rem' }}
          title={proceedButtonDisabledMessage}
          disableHoverListener={!proceedButtonDisabledMessage}
        >
          <span>
            <Button
              variant="contained"
              color="secondary"
              sx={{ ...buttonStyle, mt: 1 }}
              fullWidth
              disableElevation
              disabled={!!proceedButtonDisabledMessage}
              onClick={updatePortfolioStatus}
            >
              {proceedButtonLabel}
            </Button>
          </span>
        </Tooltip>

        <Tooltip
          placement="left"
          arrow
          sx={{ fontSize: '1rem' }}
          title={cancelButtonDisabledMessage}
          disableHoverListener={!cancelButtonDisabledMessage}
        >
          <span>
            <Button
              variant="outlined"
              color="secondary"
              sx={{ ...buttonStyle, mt: 1 }}
              fullWidth
              disableElevation
              disabled={!!cancelButtonDisabledMessage}
              onClick={cancelPortfolio}
            >
              {data?.isCanceled ? 'Restart Sale' : 'Cancel Sale'}
            </Button>
          </span>
        </Tooltip>
      </>
    )
  );
}
